import React from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { useUserAgent } from 'next-useragent';
import nookies from 'nookies';
import { hasSeo, seoData } from '../../utils/seo';
import { RootStoreProvider } from '../../providers/RootStoreProvider';
import { Layout } from '../../components/Layout';
import { SearchStoreProvider } from '../../providers/SearchStoreProvider';
import { SearchPage } from '../../components/SearchPage';
import { Subscribe } from '../../components/MainPage/Subscribe';
import { apiProxy } from '../../lib/api';
import { BreadCrumbs } from '../../components/BreadCrumbs';
import { Text } from '../../components/Text';
import { Collapsed } from '../../components/Collapsed';
import { redirectFromTo } from '../../utils/redirectFromTo';
import { logger } from '../../lib/logger';
import { containsSea } from '../../utils/containSea';

export default function ResultPage({
  uaString,
  store: { user, popdir, url, seo, banners, popularPopdirs },
  canonicalUrl,
}) {
  const ua = useUserAgent(uaString || window.navigator.userAgent);
  const { isMobile, isTablet } = ua;
  const pageRoute = [
    { title: 'Скидки и акции', link: 'https://kruiz.online/discount' },
    { title: popdir.caption, link: '' },
  ];
  const router = useRouter();

  return (
    <SearchStoreProvider hydrationData={{ user, popdir, url }}>
      <RootStoreProvider hydrationData={{ user, popdir, url }}>
        <NextSeo
          title={seo?.title ?? 'Акции'}
          description={seo?.description ?? 'Акции'}
          additionalLinkTags={[
            {
              rel: 'shortcut icon',
              href: '/favicon.ico?v=2',
            },
          ]}
          canonical={canonicalUrl}
        />
        <Layout
          isMobile={isMobile}
          isTablet={isTablet}
          withoutForm
          popularPopdirs={popularPopdirs}
        >
          <div className="container--block">
            <div style={{ marginBottom: 32 }}>
              <BreadCrumbs routeCrumbs={pageRoute} />
            </div>
            <SearchPage
              noRedirectToResult
              url={url}
              popdir={popdir}
              isMobile={isMobile}
              isDiscount
              isShoping={popdir?.extraCondition === 'black_friday'}
              urlBanners={banners}
              linkCover={
                router.query.id === 'rannee_bron' &&
                '/novogodniy-keshbek-za-pokupku-kruizov?rs=main-banner_keshbek-v-vidache'
              }
            />
            <Subscribe />
            {hasSeo(seo, popdir) && (
              <section>
                <Text component="h2" variant="heading-4">
                  Часто задаваемые вопросы
                </Text>
                <Collapsed
                  seo={seoData(seo)}
                  shorter
                  isSeo
                  onClickAction={'click_load_more_questions'}
                />
              </section>
            )}
          </div>
        </Layout>
      </RootStoreProvider>
    </SearchStoreProvider>
  );
}

export const getServerSideProps = async ctx => {
  const cookies = nookies.get(ctx);
  const { token } = cookies;

  // энкодим русские буквы для корректной работы ручки
  const encodedId = encodeURI(ctx.query.id);
  // Получаем только путь без query параметров
  const canonicalPath = ctx.req.url.split('?')[0];
  const canonicalUrl = `https://kruiz.online${canonicalPath}`;
  const hasSea = containsSea(ctx.req.url);

  const popdirPromises =
    (await apiProxy
      .get(`Tester/discount_pages/${encodedId}`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      })
      .then(({ data }) => data)
      .catch(console.log)) ?? null;

  if (popdirPromises?.redirect.code !== 200) {
    return redirectFromTo(popdirPromises?.redirect, ctx);
  }

  try {
    const userPromises =
      (await apiProxy
        .get(`ApiUsers/check_token/${token ?? ''}`, {
          headers: {
            'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
            ym_uid: cookies._ym_uid || 'ym_cid not found',
          },
        })
        .then(({ data }) => data)
        .catch(console.log)) ?? null;

    const seoPromises =
      (await apiProxy
        .get(`Discount/get_discount?id=${popdirPromises.id}`, {
          headers: {
            'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
            ym_uid: cookies._ym_uid || 'ym_cid not found',
          },
        })
        .then(({ data }) => data)
        .catch(console.log)) ?? null;
    const bannersPromise =
      (await apiProxy
        .get(`ApiBanners/getResultBanners`, {
          headers: {
            'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
            ym_uid: cookies._ym_uid || 'ym_cid not found',
          },
        })
        .then(({ data }) => data)
        .catch(console.log)) ?? null;

    const popularPopdirsPromises =
      (await apiProxy
        .get(`ApiPopdirNew/getHeaderPopdir/0`, {
          headers: {
            'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
            ym_uid: cookies._ym_uid || 'ym_cid not found',
          },
        })
        .then(({ data }) => data)
        .catch(console.log)) ?? null;

    const promises = [
      userPromises,
      popdirPromises,
      seoPromises,
      bannersPromise,
      popularPopdirsPromises,
    ];

    const responses = await Promise.allSettled(promises);

    const [user, popdir, seo, banners, popularPopdirs] = responses.map(req =>
      req.status === 'fulfilled' ? req.value ?? null : null,
    );

    if (ctx.query.id === 'black_friday') {
      return {
        redirect: {
          destination: `/black_friday`,
          permanent: true,
        },
      };
    }
    logger({
      additionalFields: {
        url: ctx.resolvedUrl,
      },
      level: 'debug',
      ym_uid: cookies._ym_uid,
      message: 'page has been created',
    });
    return {
      props: {
        store: {
          user,
          popdir,
          url: ctx.query.id,
          seo,
          banners,
          popularPopdirs,
        },
        uaString: ctx.req.headers['user-agent'],
        canonicalUrl,
      },
    };
  } catch (error) {
    logger({
      additionalFields: {
        url: ctx.resolvedUrl,
      },
      error,
      level: 'error',
      message: 'page was not created',
    });

    return redirectFromTo({ url: '', code: 503 }, ctx);
  }
};
